import { memo, useCallback, useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { WebSocketContext } from './WebSocket'
import { WebSocketRequestMessage, WebSocketRequestMessage_Subscription } from '../../proto/generated/websocket'
import { selectWebsocketSubscribeList } from '../../selectors/websocket'
import { ensureValidAccessToken, subscribeAccessTokenRefresh } from '../../services/auth/tokenRefresh'

type Props = {
    onWebSocketMessageReceived: (data: Uint8Array) => void | Promise<void>
    initialDataFlagRef: React.MutableRefObject<boolean>
}

const AppWebSocketSubscriptionSync = ({ onWebSocketMessageReceived, initialDataFlagRef }: Props) => {
    const ws = useContext(WebSocketContext)
    const subscriptionList = useSelector(selectWebsocketSubscribeList)

    const subscribeSecurities = useCallback(
        async (list: WebSocketRequestMessage_Subscription) => {
            const accessToken = await ensureValidAccessToken()
            if (!accessToken) {
                return
            }

            const request = {
                authToken: accessToken,
                subscription: list,
                sendInitialData: initialDataFlagRef.current,
            } as WebSocketRequestMessage

            if (ws !== null) {
                ws.addListener(onWebSocketMessageReceived)
                ws.sendMessage(WebSocketRequestMessage.encode(request).finish())
            }
        },
        [onWebSocketMessageReceived, ws, initialDataFlagRef]
    )

    useEffect(() => {
        void subscribeSecurities(subscriptionList)

        const unsubscribeTokenRefresh = subscribeAccessTokenRefresh(() => {
            void subscribeSecurities(subscriptionList)
        })

        return () => {
            unsubscribeTokenRefresh()
            if (ws !== null) {
                ws.removeListener(onWebSocketMessageReceived)
            }
        }
    }, [subscribeSecurities, subscriptionList, ws, onWebSocketMessageReceived])

    return null
}

export default memo(AppWebSocketSubscriptionSync)
